import {
  BACKGROUND_COLORS,
  TEXT_COLORS,
  normalizeAlignment,
  normalizeColor,
  normalizeIndent
} from "../editor/formattingConfig.mjs";
import { escapeHtml } from "./exportSerializers.mjs";

const MAX_RENDER_DEPTH = 100;
const MAX_RENDER_NODES = 10000;
const MAX_RENDER_CHARS = 250000;
const MAX_ATTRIBUTE_CHARS = 10000;

function safeArray(value, limits) {
  try {
    if (!Array.isArray(value)) return [];
    const length = value.length;
    if (!Number.isSafeInteger(length) || length < 0 || length > MAX_RENDER_NODES) {
      if (limits) limits.failed = true;
      return [];
    }
    const result = [];
    const remaining = limits ? Math.max(0, MAX_RENDER_NODES - limits.nodes + 1) : length;
    for (let index = 0; index < Math.min(length, remaining); index += 1) result.push(value[index]);
    return result;
  } catch {
    if (limits) limits.failed = true;
    return [];
  }
}

function consumeNode(limits, depth) {
  if (limits.failed || depth > MAX_RENDER_DEPTH || limits.nodes >= MAX_RENDER_NODES) {
    limits.failed = true;
    return false;
  }
  limits.nodes += 1;
  return true;
}

function consumeInput(limits, value) {
  if (limits.failed || typeof value !== "string" || value.length > MAX_RENDER_CHARS - limits.inputChars) {
    limits.failed = true;
    return false;
  }
  limits.inputChars += value.length;
  return true;
}

function emit(limits, value) {
  if (limits.failed || typeof value !== "string" || value.length > MAX_RENDER_CHARS - limits.outputChars) {
    limits.failed = true;
    return "";
  }
  limits.outputChars += value.length;
  return value;
}

function emitEscaped(limits, value, consume = true) {
  if (typeof value !== "string" || (consume && !consumeInput(limits, value))) return "";
  let escapedLength = 0;
  const remaining = MAX_RENDER_CHARS - limits.outputChars;
  for (let index = 0; index < value.length; index += 1) {
    const character = value[index];
    escapedLength += character === '"' ? 6 : character === "&" || character === "'" ? 5 : character === "<" || character === ">" ? 4 : 1;
    if (escapedLength > remaining) {
      limits.failed = true;
      return "";
    }
  }
  return emit(limits, escapeHtml(value));
}

function property(value, name) {
  try {
    return value?.[name];
  } catch {
    return undefined;
  }
}

function style(styles, name) {
  const value = property(styles, name);
  return typeof value === "string" ? value.trim() : "";
}

function mergeStyles(...values) {
  return values.map((value) => typeof value === "string" ? value.trim().replace(/;+$/u, "") : "")
    .filter(Boolean)
    .join(";");
}

function styleAttribute(value, limits) {
  if (!value) return "";
  if (typeof value !== "string" || value.length > MAX_ATTRIBUTE_CHARS) {
    limits.failed = true;
    return "";
  }
  return ` style="${escapeHtml(value)}"`;
}

function blockStyle(attrs) {
  try {
    const declarations = [];
    const alignment = normalizeAlignment(attrs?.textAlign);
    const indent = normalizeIndent(attrs?.indent);
    if (alignment) declarations.push(`text-align:${alignment}`);
    if (indent) declarations.push(`margin-left:${indent * 2}em`);
    return declarations.join(";");
  } catch {
    return "";
  }
}

function safeHttpUrl(value) {
  try {
    if (typeof value !== "string" || value.length > MAX_ATTRIBUTE_CHARS) return null;
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:" ? value : null;
  } catch {
    return null;
  }
}

function renderInlineNode(node, styles, limits, depth) {
  if (!consumeNode(limits, depth)) return "";
  try {
    const type = property(node, "type");
    if (type === "hardBreak") return emit(limits, "<br>");
    if (type !== "text") return "";
    const text = property(node, "text");
    let html = emitEscaped(limits, typeof text === "string" ? text : "");
    const seenMarks = new Set();
    for (const mark of safeArray(property(node, "marks"), limits)) {
      if (!consumeNode(limits, depth)) return "";
      const markType = property(mark, "type");
      if (seenMarks.has(markType)) continue;
      seenMarks.add(markType);
      if (markType === "bold") html = `${emit(limits, `<strong${styleAttribute(style(styles, "strong"), limits)}>`)}${html}${emit(limits, "</strong>")}`;
      else if (markType === "italic") html = `${emit(limits, `<em${styleAttribute(style(styles, "em"), limits)}>`)}${html}${emit(limits, "</em>")}`;
      else if (markType === "underline") html = `${emit(limits, `<span${styleAttribute(style(styles, "u"), limits)}>`)}${html}${emit(limits, "</span>")}`;
      else if (markType === "strike") html = `${emit(limits, `<s${styleAttribute(style(styles, "s"), limits)}>`)}${html}${emit(limits, "</s>")}`;
      else if (markType === "code") html = `${emit(limits, `<code${styleAttribute(style(styles, "codeInline") || style(styles, "code"), limits)}>`)}${html}${emit(limits, "</code>")}`;
      else if (markType === "textStyle") {
        const color = normalizeColor(property(property(mark, "attrs"), "color"), TEXT_COLORS);
        if (color) html = `${emit(limits, `<span style="color:${color}">`)}${html}${emit(limits, "</span>")}`;
      } else if (markType === "highlight") {
        const rawColor = property(property(mark, "attrs"), "color");
        const color = normalizeColor(rawColor, BACKGROUND_COLORS);
        if (color) html = `${emit(limits, `<span style="background-color:${color}">`)}${html}${emit(limits, "</span>")}`;
        else if (rawColor === undefined || rawColor === null) html = `${emit(limits, `<mark${styleAttribute(style(styles, "mark"), limits)}>`)}${html}${emit(limits, "</mark>")}`;
      } else if (markType === "link") {
        const href = safeHttpUrl(property(property(mark, "attrs"), "href"));
        if (href) {
          html = `${emit(limits, `<a${styleAttribute(style(styles, "a"), limits)} href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">`)}${html}${emit(limits, "</a>")}`;
        }
      }
      if (limits.failed) return "";
    }
    return html;
  } catch {
    return "";
  }
}

function renderInlineContent(node, styles, limits, depth) {
  let html = "";
  for (const child of safeArray(property(node, "content"), limits)) {
    html += renderInlineNode(child, styles, limits, depth + 1);
    if (limits.failed) break;
  }
  return html;
}

function rawText(node, limits, depth) {
  if (!consumeNode(limits, depth)) return "";
  try {
    if (property(node, "type") === "text") {
      const text = property(node, "text");
      if (typeof text !== "string" || !consumeInput(limits, text)) return "";
      return text;
    }
    if (property(node, "type") === "hardBreak") return "\n";
    let text = "";
    for (const child of safeArray(property(node, "content"), limits)) {
      text += rawText(child, limits, depth + 1);
      if (limits.failed) break;
    }
    return text;
  } catch {
    limits.failed = true;
    return "";
  }
}

function renderImagePlaceholder(node, styles, limits) {
  const attrs = property(node, "attrs");
  const rawTitle = property(attrs, "title");
  const rawPlacement = property(attrs, "placement");
  const rawDescription = property(attrs, "description");
  const title = typeof rawTitle === "string" ? rawTitle : "";
  const placement = typeof rawPlacement === "string" ? rawPlacement : "";
  const description = typeof rawDescription === "string" ? rawDescription : "";
  const safeTitle = emitEscaped(limits, title);
  const safePlacement = emitEscaped(limits, placement);
  const safeDescription = description
    ? `${emit(limits, "：")}${emitEscaped(limits, description)}`
    : "";
  const openOuter = emit(limits, `<section${styleAttribute(style(styles, "imageWrap"), limits)}>\n`);
  const openInner = emit(limits, `  <section${styleAttribute(style(styles, "imageFrame"), limits)}>\n`);
  const badge = emit(limits, `    <span${styleAttribute(style(styles, "imageBadge"), limits)}>图片占位</span>\n`);
  const titleHtml = `${emit(limits, `    <strong${styleAttribute(style(styles, "imageTitle"), limits)}>`)}${safeTitle}${emit(limits, "</strong>\n")}`;
  const metaHtml = `${emit(limits, `    <span${styleAttribute(style(styles, "imageMeta"), limits)}>`)}${safePlacement}${safeDescription}${emit(limits, "</span>\n")}`;
  const close = emit(limits, "  </section>\n</section>");
  return `${openOuter}${openInner}${badge}${titleHtml}${metaHtml}${close}`;
}

function renderListItem(node, styles, limits, depth, task = false) {
  if (!consumeNode(limits, depth)) return "";
  const attrs = property(node, "attrs");
  const checked = property(attrs, "checked") === true;
  const marker = task
    ? emit(limits, `<span${styleAttribute(style(styles, "taskMarker"), limits)}>${checked ? "☑" : "☐"}</span>`)
    : "";
  let body = "";
  for (const child of safeArray(property(node, "content"), limits)) {
    body += renderWechatNode(child, styles, limits, depth + 1);
    if (limits.failed) break;
  }
  const open = emit(limits, `<li${styleAttribute(style(styles, "li"), limits)}>`);
  const close = emit(limits, "</li>");
  return `${open}${marker}${body}${close}`;
}

function renderList(node, styles, limits, depth, type) {
  const task = type === "taskList";
  const tag = type === "orderedList" ? "ol" : "ul";
  const attrs = property(node, "attrs");
  const start = property(attrs, "start");
  const startAttribute = tag === "ol" && Number.isSafeInteger(start) && start > 0
    ? ` start="${start}"`
    : "";
  const taskAttribute = task ? " data-task-list=\"true\"" : "";
  const listStyle = style(styles, task ? "taskList" : "list") || style(styles, "list");
  let items = "";
  for (const item of safeArray(property(node, "content"), limits)) {
    if (property(item, "type") === (task ? "taskItem" : "listItem")) {
      items += renderListItem(item, styles, limits, depth + 1, task);
    }
    if (limits.failed) break;
  }
  const open = emit(limits, `<${tag}${styleAttribute(mergeStyles(listStyle, blockStyle(attrs)), limits)}${taskAttribute}${startAttribute}>`);
  const close = emit(limits, `</${tag}>`);
  return `${open}${items}${close}`;
}

function renderWechatNode(node, styles, limits, depth = 0, decorateH2 = false) {
  if (!consumeNode(limits, depth)) return "";
  try {
    const type = property(node, "type");
    const attrs = property(node, "attrs");
    if (type === "heading") {
      const rawLevel = property(attrs, "level");
      const level = Number.isInteger(rawLevel) && rawLevel >= 1 && rawLevel <= 5 ? rawLevel : 2;
      const open = emit(limits, `<h${level}${styleAttribute(mergeStyles(style(styles, `h${level}`), blockStyle(attrs)), limits)}>`);
      const decoration = level === 2 && decorateH2 ? renderH2Decoration(styles, limits) : "";
      const content = renderInlineContent(node, styles, limits, depth);
      const close = emit(limits, `</h${level}>`);
      return `${open}${decoration}${content}${close}`;
    }
    if (type === "paragraph") {
      const open = emit(limits, `<p${styleAttribute(mergeStyles(style(styles, "p"), blockStyle(attrs)), limits)}>`);
      const content = renderInlineContent(node, styles, limits, depth);
      const close = emit(limits, "</p>");
      return `${open}${content}${close}`;
    }
    if (type === "blockquote") {
      const quoteStyle = style(styles, "quoteMark");
      const quote = quoteStyle
        ? emit(limits, `<span${styleAttribute(quoteStyle, limits)}>“</span>`)
        : "";
      let body = "";
      for (const child of safeArray(property(node, "content"), limits)) {
        body += renderWechatNode(child, styles, limits, depth + 1);
        if (limits.failed) break;
      }
      const open = emit(limits, `<blockquote${styleAttribute(mergeStyles(style(styles, "blockquote"), blockStyle(attrs)), limits)}>`);
      const close = emit(limits, "</blockquote>");
      return `${open}${quote}${body}${close}`;
    }
    if (["bulletList", "orderedList", "taskList"].includes(type)) return renderList(node, styles, limits, depth, type);
    if (type === "codeBlock") {
      const text = rawText(node, limits, depth + 1);
      const open = emit(limits, `<pre${styleAttribute(mergeStyles(style(styles, "codeBlock"), blockStyle(attrs)), limits)}><code${styleAttribute(style(styles, "code"), limits)}>`);
      const content = emitEscaped(limits, text, false);
      const close = emit(limits, "</code></pre>");
      return `${open}${content}${close}`;
    }
    if (type === "horizontalRule") return emit(limits, `<hr${styleAttribute(mergeStyles(style(styles, "hr"), blockStyle(attrs)), limits)}>`);
    if (type === "imagePlaceholder") return renderImagePlaceholder(node, styles, limits);
    return "";
  } catch {
    limits.failed = true;
    return "";
  }
}

function renderH2Decoration(styles, limits) {
  const decoration = style(styles, "h2Prefix") || style(styles, "h2Square") || style(styles, "h2Diamond");
  return decoration ? emit(limits, `<span${styleAttribute(decoration, limits)}></span>`) : "";
}

function hasRole(node, role) {
  return property(property(node, "attrs"), "role") === role;
}

function renderRoot({ title, summary, body, styles, limits }) {
  const safeTitle = (typeof title === "string" ? title : String(title || "")).trim() || "未命名文章";
  const heroStyle = style(styles, "hero") || "margin-bottom:6px;";
  const titleRule = style(styles, "titleRule")
    ? emit(limits, `<p${styleAttribute(style(styles, "titleRule"), limits)}>&nbsp;</p>`)
    : "";
  const summaryLabel = style(styles, "summaryLabel")
    ? emit(limits, `<span${styleAttribute(style(styles, "summaryLabel"), limits)}>摘要</span>`)
    : "";
  const summaryHtml = summary === null
    ? ""
    : `${emit(limits, `<p${styleAttribute(style(styles, "summary"), limits)}>`)}${summaryLabel}${summary}${emit(limits, "</p>")}`;
  const safeTitleHtml = emitEscaped(limits, safeTitle);
  const openRoot = emit(limits, `<section${styleAttribute(style(styles, "root"), limits)}>\n`);
  const openHero = emit(limits, `<section${styleAttribute(heroStyle, limits)}>\n`);
  const titleHtml = `${emit(limits, `  <h1${styleAttribute(style(styles, "h1"), limits)}>`)}${safeTitleHtml}${emit(limits, "</h1>\n")}`;
  const titleRuleLine = `${emit(limits, "  ")}${titleRule}${emit(limits, "\n")}`;
  const summaryLine = `${emit(limits, "  ")}${summaryHtml}${emit(limits, "\n")}`;
  const closeHero = emit(limits, "</section>\n");
  const closeRoot = emit(limits, "\n</section>");
  return `${openRoot}${openHero}${titleHtml}${titleRuleLine}${summaryLine}${closeHero}${body}${closeRoot}`;
}

export function renderTiptapWechatDocument(document, options) {
  try {
    const limits = { nodes: 0, inputChars: 0, outputChars: 0, failed: false };
    const title = property(options, "title");
    const styles = property(options, "styles") || {};
    const content = safeArray(property(document, "content"), limits);
    let summaryNode = null;
    for (const node of content) {
      if (hasRole(node, "summary")) {
        summaryNode = node;
        break;
      }
    }
    let summary = null;
    if (summaryNode) {
      if (!consumeNode(limits, 0)) return "";
      summary = renderInlineContent(summaryNode, styles, limits, 0);
    }
    let body = "";
    let sectionOpen = false;
    for (const node of content) {
      if (hasRole(node, "title") || hasRole(node, "summary")) continue;
      const attrs = property(node, "attrs");
      const startsSection = property(node, "type") === "heading" && property(attrs, "level") === 2;
      if (startsSection) {
        if (sectionOpen) body += emit(limits, "</section>\n");
        body += emit(limits, `<section${styleAttribute(style(styles, "section"), limits)}>\n`);
        sectionOpen = true;
      } else if (body) {
        body += emit(limits, "\n");
      }
      body += renderWechatNode(node, styles, limits, 0, startsSection);
      if (limits.failed) break;
    }
    if (sectionOpen) body += emit(limits, "\n</section>");
    const html = renderRoot({ title, summary, body, styles, limits });
    return limits.failed ? "" : html;
  } catch {
    return "";
  }
}
